import React from 'react';
import { Button, Divider, Form, Message } from 'semantic-ui-react';

const fields = [
  ['input_price', '输入'],
  ['output_price', '输出'],
  ['cache_read_price', '缓存命中输入'],
  ['cache_creation_price', '缓存写入'],
];

export function validateTimePricing(model) {
  const rule = model.time_pricing;
  if (!rule) return;
  if (model.type !== 'simple' || model.context_rules?.length)
    throw new Error('峰谷计费仅支持无上下文规则的简单定价');
  if (rule.enabled && !model.uniform_pricing)
    throw new Error('峰谷计费需要模型统一定价');
  try {
    if (!rule.timezone || rule.timezone === 'Local') throw new Error();
    new Intl.DateTimeFormat('en', { timeZone: rule.timezone });
  } catch (_) {
    throw new Error('请填写有效时区，例如 Asia/Shanghai');
  }
  for (const [field, label] of fields) {
    const peak = model[field] || {};
    const valley = rule.off_peak_prices?.[field] || {};
    if (
      ['input_price', 'output_price'].includes(field) &&
      !Object.keys(peak).length
    )
      throw new Error(`${label}至少填写一种币种的峰价和谷价`);
    for (const currency of ['cny', 'usd']) {
      const a = peak[currency];
      const b = valley[currency];
      if (a == null && b == null) continue;
      if (
        ![a, b].every(
          (value) => value != null && /^\d+(\.\d+)?$/.test(String(value))
        )
      )
        throw new Error(
          `${label} ${currency.toUpperCase()} 的峰价和谷价必须同时填写非负单价`
        );
    }
  }
  const occupied = new Set();
  if (!rule.peak_windows?.length) throw new Error('请至少添加一个峰时时段');
  for (const window of rule.peak_windows) {
    const valid = (value, end) =>
      /^([01]\d|2[0-3]):[0-5]\d$/.test(value) || (end && value === '24:00');
    if (
      !valid(window.start, false) ||
      !valid(window.end, true) ||
      window.start >= window.end
    )
      throw new Error(
        '时段需为 HH:MM，开始早于结束；跨天请拆分，结束可填 24:00'
      );
    if (!window.weekdays?.length) throw new Error('每个峰时时段至少选择一天');
    const minutes = (value) =>
      Number(value.slice(0, 2)) * 60 + Number(value.slice(3));
    for (const day of window.weekdays) {
      for (
        let minute = minutes(window.start);
        minute < minutes(window.end);
        minute++
      ) {
        const key = `${day}-${minute}`;
        if (occupied.has(key)) throw new Error('峰时时段不能重叠');
        occupied.add(key);
      }
    }
  }
}

export default function TimePricingEditor({ model, onChange }) {
  const rule = model.time_pricing;
  const updateRule = (patch) =>
    onChange({ ...model, time_pricing: { ...rule, ...patch } });
  const changePrice = (field, currency, valley, value) => {
    const parent = valley ? rule.off_peak_prices : model;
    const price = { ...parent?.[field] };
    if (value === '') delete price[currency];
    else price[currency] = value;
    if (valley) {
      const prices = { ...rule.off_peak_prices };
      if (Object.keys(price).length) prices[field] = price;
      else delete prices[field];
      updateRule({ off_peak_prices: prices });
    } else onChange({ ...model, [field]: price });
  };
  return (
    <>
      <Divider horizontal>模型峰谷定价</Divider>
      <Form.Checkbox
        label='启用峰谷计费'
        aria-label='启用峰谷计费'
        checked={!!rule?.enabled}
        disabled={!!model.context_rules?.length}
        onChange={(_, { checked }) =>
          onChange({
            ...model,
            uniform_pricing: checked ? true : model.uniform_pricing,
            time_pricing: {
              timezone: 'Asia/Shanghai',
              peak_windows: [
                { weekdays: [1, 2, 3, 4, 5], start: '09:00', end: '18:00' },
              ],
              off_peak_prices: {},
              ...rule,
              enabled: checked,
            },
          })
        }
      />
      {model.context_rules?.length > 0 && (
        <Message>请先移除上下文规则，再启用峰谷计费。</Message>
      )}
      {rule && (
        <>
          <Message info>
            模型统一定价，不受渠道倍率影响。填写具体单价，单位为元或美元/百万
            token；0
            表示免费，留空表示未配置。关闭后按峰价计费。缓存价格两侧均留空时按各自输入价计费。
          </Message>
          {fields.map(([field, label]) => (
            <React.Fragment key={field}>
              <strong>{label}</strong>
              {['cny', 'usd'].map((currency) => (
                <Form.Group widths='equal' key={currency}>
                  {[false, true].map((valley) => {
                    const name = `${label} ${currency.toUpperCase()} ${
                      valley ? '谷价' : '峰价'
                    }`;
                    return (
                      <Form.Input
                        key={name}
                        label={name}
                        aria-label={name}
                        inputMode='decimal'
                        value={
                          (valley
                            ? rule.off_peak_prices?.[field]?.[currency]
                            : model[field]?.[currency]) ?? ''
                        }
                        onChange={(_, { value }) =>
                          changePrice(field, currency, valley, value)
                        }
                      />
                    );
                  })}
                </Form.Group>
              ))}
            </React.Fragment>
          ))}
          <Form.Input
            label='计费时区'
            aria-label='计费时区'
            value={rule.timezone}
            onChange={(_, { value }) => updateRule({ timezone: value })}
          />
          <p>
            以下为峰时，其余为谷时；按请求开始时间计费。开始时刻包含，结束时刻不包含。
          </p>
          {(rule.peak_windows || []).map((window, index) => {
            const changeWindow = (patch) =>
              updateRule({
                peak_windows: rule.peak_windows.map((item, i) =>
                  i === index ? { ...item, ...patch } : item
                ),
              });
            return (
              <div key={index}>
                <Form.Group inline>
                  {['一', '二', '三', '四', '五', '六', '日'].map((day, i) => (
                    <Form.Checkbox
                      key={day}
                      label={`周${day}`}
                      aria-label={`时段${index + 1}周${day}`}
                      checked={window.weekdays.includes(i + 1)}
                      onChange={(_, { checked }) =>
                        changeWindow({
                          weekdays: checked
                            ? [...window.weekdays, i + 1].sort()
                            : window.weekdays.filter((d) => d !== i + 1),
                        })
                      }
                    />
                  ))}
                </Form.Group>
                <Form.Group widths='equal'>
                  <Form.Input
                    label='开始'
                    aria-label={`时段${index + 1}开始`}
                    value={window.start}
                    placeholder='09:00'
                    onChange={(_, { value }) => changeWindow({ start: value })}
                  />
                  <Form.Input
                    label='结束'
                    aria-label={`时段${index + 1}结束`}
                    value={window.end}
                    placeholder='24:00'
                    onChange={(_, { value }) => changeWindow({ end: value })}
                  />
                </Form.Group>
                <Button
                  type='button'
                  size='mini'
                  onClick={() =>
                    updateRule({
                      peak_windows: rule.peak_windows.filter(
                        (_, i) => i !== index
                      ),
                    })
                  }
                >
                  删除时段 {index + 1}
                </Button>
                <Divider />
              </div>
            );
          })}
          <Button
            type='button'
            onClick={() =>
              updateRule({
                peak_windows: [
                  ...(rule.peak_windows || []),
                  { weekdays: [1, 2, 3, 4, 5], start: '18:00', end: '24:00' },
                ],
              })
            }
          >
            添加峰时时段
          </Button>
        </>
      )}
    </>
  );
}
